'use client'

import { metricNow } from './metrics'
import {
  comparePageRouteSpecificity,
  matchesPageRoute,
  type PageCommitContract,
} from './page-contracts'
import { createPageReadinessTelemetry } from './page-readiness-browser'

type RuntimePageCommitContract = Pick<
  PageCommitContract,
  'route' | 'aliases' | 'phase' | 'domain'
>

/** Actual page component effects close this population; this never asserts data readiness. */
export function createPageCommitTelemetry<Key extends string>(
  contracts: Readonly<Record<Key, RuntimePageCommitContract>>
) {
  const gates = Object.fromEntries(
    Object.entries<RuntimePageCommitContract>(contracts)
      .sort(([, left], [, right]) =>
        comparePageRouteSpecificity(left.route, right.route)
      )
      .map(([key, contract]) => [
        key,
        {
          domain: contract.domain,
          phase: contract.phase,
          matches: (path: string) =>
            [contract.route, ...(contract.aliases ?? [])].some((route) =>
              matchesPageRoute(route, path)
            ),
        },
      ])
  ) as Record<
    Key,
    { domain: string; phase: string; matches: (path: string) => boolean }
  >
  const telemetry = createPageReadinessTelemetry(gates, {
    operation: 'client.page.commit',
    readyReason: 'component_committed',
    useAttachedSurface: true,
  })
  return {
    ...telemetry,
    attach: (options: Parameters<typeof telemetry.attach>[0]) => {
      const lease = telemetry.attach(options)
      return {
        release: lease.release,
        update: (state: Parameters<typeof lease.update>[0]) => {
          const completedAt = metricNow()
          // A reused page can contain another page. The outer committed marker owns
          // this population; defer one microtask so its lease replaces the inner one.
          try {
            queueMicrotask(() => lease.update(state, completedAt))
          } catch {
            lease.update(state, completedAt)
          }
        },
      }
    },
  }
}
